import type { ZudokuConfig } from "zudoku";

const config: ZudokuConfig = {
  site: {
    showPoweredBy: false,
    logo: {
      src: { light: "/logo-light.svg", dark: "/logo-dark.svg" },
      alt: "Chameleoon",
      width: "130px"
    }
  },
  metadata: {
    favicon: "https://static.chameleoon.sk/img/favicon.ico",
    title: '%s'
  },
  docs: {
    // Routes are derived by stripping the glob parent, so
    // /pages-mcp/mcp-pripojenie.mdx is served at /mcp-pripojenie.
    files: ["/pages/**/*.{md,mdx}", "/pages-mcp/**/*.{md,mdx}"],
    defaultOptions: {
      showLastModified: false
    }
  },
  sitemap: {
    siteUrl: "https://docs.chameleoon.sk",
    // Keep the v1 API reference (/api tree) out of the sitemap. Zudoku's
    // `exclude` is exact-match only (no prefix/glob), so every generated
    // /api page is listed here. Add a line when a new v1 API tag is added.
    exclude: [
      "/api",
      "/api/couriers",
      "/api/configuration",
      "/api/validation",
      "/api/shipping-orders",
      "/api/shipping-labels",
      "/api/pickup",
      "/api/~endpoints",
      "/api/~schemas",
      "/api-v2/~schemas",
      "/mcp/~schemas",
    ],
  },
  navigation: [
    {
      type: "category",
      label: "Dokumentácia",
      items: [
        {
          type: "category",
          label: "Úvod",
          icon: "sparkles",
          collapsible: false,
          items: [
            "/introduction",
            { type: "doc", file: "/uvod-k-api", label: "Úvod k API" },
            {
              type: "link",
              icon: "folder-cog" as const,
              badge: {
                label: "v1",
                color: "blue",
              },
              label: "API v1 dokumentácia",
              to: "/api",
            },
            {
              type: "link",
              icon: "folder-cog",
              badge: {
                label: "v2",
                color: "purple",
              },
              label: "API v2 dokumentácia",
              to: "/api-v2",
            },
            {
              type: "link",
              icon: "bot",
              badge: {
                label: "MCP",
                color: "green",
              },
              label: "MCP server",
              to: "/mcp",
            },
            {
              type: "doc",
              file: "/mcp-pripojenie",
              label: "Pripojenie AI asistenta",
            },
          ],
        },
        {
          type: "category",
          label: "Aplikácia Chameleoon",
          icon: "layout-dashboard",
          items: [
            "/chameleoon-aplikacia",
            "/nastavenie-aplikacie",
            "/kontrola-duplicity",
          ],
        },
        {
          type: "category",
          label: "Kontrola výdaja",
          icon: "clipboard-check",
          items: [
            "/kontrola-vydaja",
            "/kontrola-zo-stavu",
          ],
        },
        {
          type: "category",
          label: "Sledovanie zásielok",
          icon: "map-pin",
          items: [
            "/sledovanie-zasielok",
            "/sledovacia-stranka",
          ],
        },
        {
          type: "category",
          label: "Vychystanie a balenie",
          icon: "package",
          items: [
            "/vychystanie-a-balenie",
            "/nastavenie-vychystavania",
            "/nastavenie-balenia",
            "/priebeh-vychystania",
            "/priebeh-balenia",
          ],
        },
        {
          type: "category",
          label: "Váženie balíkov",
          icon: "scale",
          items: [
            "/vazenie-balikov",
            "/vazenie-expedicia",
            "/vazenie-konfiguracia",
            "/vazenie-konfiguracia-app",
            "/vazenie-checkout",
          ],
        },
        {
          type: "category",
          label: "Kuriéri",
          icon: "truck",
          items: [
            { type: "doc", file: "/kurieri", label: "Kuriéri" },
            { type: "doc", file: "/boxpi", label: "Boxpi" },
            { type: "doc", file: "/brt", label: "BRT" },
            { type: "doc", file: "/cargus", label: "Cargus" },
            { type: "doc", file: "/ceska-posta", label: "Česká pošta" },
            { type: "doc", file: "/depo", label: "DEPO" },
            { type: "doc", file: "/dhl-express", label: "DHL Express" },
            { type: "doc", file: "/dpd", label: "DPD Slovensko" },
            { type: "doc", file: "/dpd-cz", label: "DPD Česko" },
            { type: "doc", file: "/expressone", label: "Express One" },
            { type: "doc", file: "/foxpost", label: "FoxPost" },
            { type: "doc", file: "/geis", label: "GEIS" },
            { type: "doc", file: "/gls", label: "GLS" },
            { type: "doc", file: "/inpost", label: "InPost" },
            { type: "doc", file: "/kolos", label: "Kolos" },
            { type: "doc", file: "/madarska-posta", label: "Maďarská pošta" },
            { type: "doc", file: "/neoship", label: "Neoship" },
            { type: "doc", file: "/packeta", label: "Packeta" },
            "/pbh",
            { type: "doc", file: "/ppl", label: "PPL" },
            { type: "doc", file: "/qdl", label: "QDL" },
            { type: "doc", file: "/sds", label: "SDS" },
            "/slovenska-posta",
            { type: "doc", file: "/sps", label: "SPS" },
            { type: "doc", file: "/toptrans", label: "TopTrans" },
            { type: "doc", file: "/one", label: "One" },
            "/vlastna-doprava",
          ],
        },
        {
          type: "category",
          label: "Prepojenia",
          icon: "plug",
          items: [
            { type: "doc", file: "/zdrojove-systemy", label: "Zdrojové systémy" },
            { type: "doc", file: "/creative-sites", label: "Creative Sites" },
            {
              type: "category",
              label: "Externý systém",
              items: [
                { type: "doc", file: "/external-system", label: "Externý systém" },
                "/api-v3",
              ],
            },
            { type: "doc", file: "/fastcentrik", label: "FastCentrik" },
            { type: "doc", file: "/flexibee", label: "FlexiBee" },
            "/kros-fakturacia",
            { type: "doc", file: "/money-s3", label: "Money S3" },
            { type: "doc", file: "/money", label: "Money ERP" },
            { type: "doc", file: "/oberon", label: "Oberon" },
            "/oberon-server",
            { type: "doc", file: "/onix", label: "ONIX" },
            { type: "doc", file: "/pohoda", label: "Pohoda" },
            {
              type: "category",
              label: "PrestaShop",
              items: [
                { type: "doc", file: "/prestashop", label: "PrestaShop" },
                "/prestashop-api-key-16",
                "/prestashop-couriers-16",
                "/prestashop-api-key-17",
                "/prestashop-couriers-17",
                "/prestashop-api-key-18",
                "/prestashop-couriers-18",
              ],
            },
            { type: "doc", file: "/redcart", label: "RedCart" },
            { type: "doc", file: "/shoptet", label: "Shoptet" },
            { type: "doc", file: "/shopify", label: "Shopify" },
            { type: "doc", file: "/superfaktura", label: "SuperFaktúra" },
            {
              type: "category",
              label: "Upgates",
              items: [
                { type: "doc", file: "/upgates", label: "Upgates" },
                "/migracia-upgates",
              ],
            },
            { type: "doc", file: "/webareal", label: "Webareal" },
            { type: "doc", file: "/woocommerce", label: "WooCommerce" },
          ],
        },
        {
          type: "category",
          label: "FAQ",
          icon: "circle-help",
          items: [
            "/faq-aplikacia",
            "/faq-automaticka-tlac",
            { type: "doc", file: "/faq-kurieri", label: "FAQ kuriéri" },
            { type: "doc", file: "/faq-kredit", label: "FAQ kredit" },
          ],
        },
        {
          type: "category",
          label: "Novinky",
          icon: "newspaper",
          items: [
            "/novinky-januar-2026",
            "/novinky-oktober-2025",
            "/novinky-marec-2025",
            "/novinky-januar-2025",
            "/novinky-oktober-2024",
            "/novinky-september-2024",
            "/novinky-august-2024",
            "/novinky-jul-2024",
            "/novinky-maj-2024",
            "/novinky-februar-2024",
            "/novinky-januar-2024",
          ],
        },
      ],
    },
    {
      type: "link",
      to: "/api",
      label: "API v1 dokumentácia",
      badge: {
        label: "v1",
        color: "blue",
      },
    },
    {
      type: "link",
      to: "/api-v2",
      label: "API v2 dokumentácia",
      badge: {
        label: "v2",
        color: "purple",
      },
    },
    {
      type: "link",
      to: "/mcp",
      label: "MCP server",
      badge: {
        label: "MCP",
        color: "green",
      },
    },
  ],
  redirects: [{ from: "/", to: "/introduction" }],
  apis: [
    {
      type: "file",
      input: "./apis/openapi.yaml",
      path: "/api",
    },
    {
      type: "file",
      input: "./apis/openapi-v2.yaml",
      path: "/api-v2",
    },
    {
      type: "file",
      input: "./apis/mcp.yaml",
      path: "/mcp",
    },
  ]
};

export default config;
